/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareNodes } from '@fortawesome/free-solid-svg-icons';
import QRCode from 'qrcode';
import ReactGA from 'react-ga';
import Modal from '../../components/modal/Modal';
import styles from './B2bLanding.module.css';
import { openEmsMarketplaceUrl } from '../../utils/openEmsUrls';
import { shareViaApps } from '../../utils/shareViaApps';
import '../startPage/startPage.css';

export function buildB2bSectionShareUrl(section) {
  if (section === 'marketplace') {
    return openEmsMarketplaceUrl(typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null);
  }
  if (typeof window === 'undefined') return `/b2b?section=${section}`;
  return `${window.location.origin}/b2b?section=${encodeURIComponent(section)}`;
}

export default function B2bAccordionShareButton({ section, titleKey, textKey }) {
  const { t } = useTranslation();
  const [qrOpen, setQrOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const shareUrl = useMemo(() => buildB2bSectionShareUrl(section), [section]);
  const titleId = `b2b-share-qr-${section}`;
  const title = t(titleKey);
  const shareText = t(textKey);

  useEffect(() => {
    if (!qrOpen || !shareUrl) {
      setQrDataUrl('');
      return undefined;
    }

    let active = true;
    QRCode.toDataURL(shareUrl, {
      errorCorrectionLevel: 'M',
      margin: 4,
      width: 512,
      color: {
        dark: '#000000',
        light: '#ffffff',
      },
    })
      .then(url => {
        if (active) setQrDataUrl(url);
      })
      .catch(() => {
        if (active) setQrDataUrl('');
      });

    return () => {
      active = false;
    };
  }, [qrOpen, shareUrl]);

  useEffect(() => {
    if (!qrOpen) {
      setCopied(false);
    }
  }, [qrOpen]);

  const copyLink = async () => {
    setCopied(true);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        return;
      }
    } catch {
      // Fall through to a hidden textarea copy for older webviews.
    }
    try {
      const input = document.createElement('textarea');
      input.value = shareUrl;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.left = '-9999px';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    } catch {
      // Keep the copied message visible even if the environment blocks clipboard APIs.
    }
  };

  const openShareFallback = () => {
    ReactGA.event({
      category: 'share',
      action: 'b2b section share modal',
      label: String(section ?? ''),
    });
    setQrOpen(true);
  };

  const handleShareClick = () => {
    void copyLink();
    openShareFallback();
  };

  const handleShareViaApps = () => {
    ReactGA.event({
      category: 'share',
      action: 'b2b section share via apps',
      label: String(section ?? ''),
    });
    void shareViaApps({
      title,
      text: shareText,
      url: shareUrl,
    });
  };

  return (
    <>
      <div className={styles.marketplaceShareRow}>
        <button
          type="button"
          className={styles.marketplaceShareBtn}
          aria-label={t('b2bMarketplaceShareBtn')}
          aria-haspopup="dialog"
          onClick={handleShareClick}
        >
          <FontAwesomeIcon icon={faShareNodes} aria-hidden />
          <span>{t('b2bMarketplaceShareBtn')}</span>
        </button>
      </div>

      <Modal modalOpen={qrOpen} setModalOpen={setQrOpen} scrollable>
        <div className="start-page-share-qr-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
          <h2 id={titleId} className="start-page-share-qr-title">
            {title}
          </h2>
          {qrDataUrl ? (
            <button
              type="button"
              className="start-page-share-qr-copy-hit"
              onClick={copyLink}
              aria-label={copied ? t('b2bMarketplaceShareCopied') : t('b2bSectionShareCopyLink')}
            >
              <span className="start-page-share-qr-frame">
                <img src={qrDataUrl} alt={t('b2bSectionShareQrAlt')} className="start-page-share-qr-image" />
              </span>
            </button>
          ) : (
            <div className="start-page-share-qr-placeholder" aria-hidden />
          )}
          <button type="button" className="start-page-share-qr-url" onClick={copyLink}>
            {shareUrl}
          </button>
          {copied ? (
            <p className="start-page-share-qr-copied" role="status">
              {t('b2bMarketplaceShareCopied')}
            </p>
          ) : null}
          <div className="start-page-share-qr-actions">
            <button
              type="button"
              className="b2b-btn b2b-btn--outline start-page-share-qr-download-btn"
              onClick={handleShareViaApps}
            >
              {t('b2bMarketplaceShareBtn')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
